import React, {useState, useEffect, useMemo} from "react";
import styled from "styled-components";
import {Row, Card, Form, Col} from "react-bootstrap";
import ValidationError from '../../../services/ValidationError';
import SuccessButton from '../../organisms/SuccessButton';
import ServerValidationErrorAlert from '../../organisms/ServerValidationErrorAlert';
import ServerErrorModal from '../../organisms/ServerErrorModal';
import ServerErrorAlert from '../../organisms/ServerErrorAlert';
import LoadingScreen from '../../organisms/LoadingScreen';
import CardTitle from "../../organisms/CardTitle";
import {
    FormState,
    formStateAServicioTransporte,
    FormValue,
    initialFormState,
    invalidateStates,
    servicioTransporteAFormState
} from './FormState';
import {capitalize} from '../../../utils/textUtils';
import {ValidationErrorResponse} from '../../../services/ValidationError';
import {useParams} from 'react-router-dom';
import {StyledFormControl} from "../../organisms/StyledFormControl";
import CurrencyFormat from "react-currency-format";
import {Typeahead} from "react-bootstrap-typeahead";
import {useLeerSectoresOrigen} from "../../../services/paisesServicios";
import {useMutation} from "@tanstack/react-query";
import {reemplazarServicioTransporte, useBuscarServicioTransporte} from "../../../services/transporteServicios";
import Exito, {ExitoOption} from "../../organisms/Exito";

interface Option {
    id: string;
    nombre: string;
}

function FormSection() {
    const {idServicio} = useParams();
    const {isLoading, data, isSuccess} = useBuscarServicioTransporte(idServicio);
    const {isSuccess: sectoresIsSuccess, data: sectoresData} = useLeerSectoresOrigen();
    const [validatedForm, setValidatedForm] = useState(false);
    const [formState, setFormState] = useState(initialFormState);
    const [serverFailedValidations, setServerFailedValidations] = useState({} as ValidationErrorResponse);
    const [serverErrorOcurred, setServerErrorOcurred] = useState(false);
    const [isFinished, setIsFinished] = useState(false);

    const reemplazo = useMutation(reemplazarServicioTransporte, {
        onSuccess: (data, variables, context) => {
            setValidatedForm(true);
            setIsFinished(true);
        },
        onError: (error, variables, context) => {
            if (error instanceof ValidationError) {
                invalidateStates(formState, error.getFailedValidations());
                setServerFailedValidations(error.getFailedValidations());
            } else {
                setServerErrorOcurred(true);
            }
        }
    });

    const sectores: Option[] = useMemo(() => {
        if (sectoresIsSuccess && sectoresData) {
            return sectoresData.map(s => ({id: s.id, nombre: s.nombre}))
        }
        return []
    }, [sectoresIsSuccess, sectoresData]);

    useEffect(() => {
        if (isSuccess && data) {
            setFormState(servicioTransporteAFormState(data));
        }
    }, [isSuccess, data]);

    const handleSubmit = (event: React.SyntheticEvent) => {
        event.preventDefault();

        const form = event.currentTarget as HTMLFormElement;

        if (!form.checkValidity()) {
            event.stopPropagation();
        } else {
            save();
        }

        setValidatedForm(true);
    };

    const onInputChanged = ({target}: any) => {
        const newInputState = {
            ...formState[target.name as keyof FormState],
            value: target.value,
            isValid: target.checkValidity(),
            wasTouched: true
        };
        setFormState({...formState, [target.name]: newInputState});
    }

    const onInputChangedCapital = ({target}: any) => {
        const newInputState = {
            ...formState[target.name as keyof FormState],
            value: capitalize(target.value),
            isValid: target.checkValidity()
        };
        setFormState({...formState, [target.name]: newInputState});
    }

    const handleBlur = ({target}: any) => {
        const newInputState = {...formState[target.name as keyof FormState], wasTouched: true};
        setFormState({...formState, [target.name]: newInputState});
    }

    const onTypeAheadChanged = (selected: any[], name: string, options: any[]) => {
        if (selected && selected.length > 0) {
            const selectedValueId = selected[0]["id"];
            const validValue = options.find(opt => opt["id"] === selectedValueId);
            const newInputState = {
                ...formState[name as keyof FormState],
                value: selectedValueId,
                isValid: validValue,
                wasTouched: true
            };
            setFormState({...formState, [name]: newInputState});
        } else {
            const newInputState = {...formState[name as keyof FormState], value: "", isValid: true, wasTouched: true};
            setFormState({...formState, [name]: newInputState});
        }
    }

    const save = async () => {
        setServerFailedValidations({} as ValidationErrorResponse);
        setServerErrorOcurred(false);
        if (idServicio) {
            reemplazo.mutate(formStateAServicioTransporte(idServicio, formState));
        }
    }

    if(isFinished){
        return (<Exito title={'¡Modificación exitosa!'} options={exitoOptions}/>);
    }

    return (
        <Row className="justify-content-center">
            <div className="col-xs-12 col-md-10 col-lg-8 col-xxl-5">
                <LoadingScreen isLoading={isLoading}/>
                <Card>
                    <StyledCardHeader className="pt-4 pb-4">
                        <CardTitle>Modifica una tarifa de transporte</CardTitle>
                    </StyledCardHeader>
                    <Card.Body>

                        {
                            serverFailedValidations && Object.keys(serverFailedValidations).length > 0 &&
                            <Row className="justify-content-center mb-3">
                                <ServerValidationErrorAlert serverFailedValidations={serverFailedValidations}
                                                            wrapperClass="col col-9"/>
                            </Row>
                        }

                        {
                            serverErrorOcurred &&
                            <Row className="justify-content-center mb-3">
                                <ServerErrorAlert class="col col-9"/>
                            </Row>
                        }

                        {
                            serverErrorOcurred &&
                            <ServerErrorModal/>
                        }

                        <Row className="justify-content-center">
                            <Form className="col col-9" noValidate validated={validatedForm}
                                  onSubmit={handleSubmit}>
                                <Form.Group as={Col} controlId="formNombre" className="mb-2">
                                    <Form.Label>Nombre</Form.Label>
                                    <StyledFormControl type="text" name="nombre"
                                                       value={formState.nombre.value} onChange={onInputChangedCapital}
                                                       required
                                                       onBlur={handleBlur}
                                                       isValid={isRequiredFormValueValid(formState.nombre)}
                                                       isInvalid={isRequiredFormValueInvalid(formState.nombre)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Campo debe ser diligenciado.
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group as={Col} controlId="formDescripcion" className="mb-2">
                                    <Form.Label>Descripción</Form.Label>
                                    <Form.Control as="textarea" name="descripcion"
                                                  value={formState.descripcion.value} onChange={onInputChanged}
                                                  onBlur={handleBlur} rows={3}
                                                  isValid={isFormValueValid(formState.descripcion)}
                                                  isInvalid={isFormValueInvalid(formState.descripcion)}
                                    />
                                    <Form.Text muted>
                                        Opcional.
                                    </Form.Text>
                                </Form.Group>

                                {
                                  isSuccess &&
                                    <Form.Group as={Col} controlId="formSector" className="mb-2">
                                        <Form.Label>Sector</Form.Label>
                                        <Typeahead
                                            defaultSelected={[data?.sector ? data?.sector as Option : []]}
                                            clearButton
                                            id="input-sector"
                                            options={sectores}
                                            labelKey='nombre'
                                            onChange={(selected) => onTypeAheadChanged(selected, "sector", sectores)}
                                            isValid={isFormValueValid(formState.sector)}
                                            isInvalid={isFormValueInvalid(formState.sector)}
                                        />
                                        <Form.Text muted>
                                            Opcional.
                                        </Form.Text>
                                    </Form.Group>
                                }

                                <Form.Group as={Col} controlId="formTipoTrayecto" className="mb-2">
                                    <Form.Label>Número de trayectos</Form.Label>
                                    <Form.Check
                                        name="tipoTrayecto"
                                        type="radio"
                                        label="Uno"
                                        id={`check-un-trayecto`}
                                        required
                                        value={'UN_TRAYECTO'}
                                        onChange={onInputChanged}
                                        checked={formState.tipoTrayecto.value === 'UN_TRAYECTO'}
                                        />
                                    <Form.Check
                                        name="tipoTrayecto"
                                        type="radio"
                                        label="Dos"
                                        id={`check-dos-trayectos`}
                                        required
                                        value={'DOS_TRAYECTOS'}
                                        onChange={onInputChanged}
                                        checked={formState.tipoTrayecto.value === 'DOS_TRAYECTOS'}
                                        />    
                                </Form.Group>


                                <Form.Group as={Col} controlId="formPrecio" className="mb-5">
                                    <Form.Label>Precio</Form.Label>
                                    <CurrencyFormat customInput={Form.Control} name="precio"
                                                    value={formState.precio.value}
                                                    thousandSeparator={","}
                                                    onBlur={handleBlur}
                                                    isValid={isRequiredFormValueValid(formState.precio)}
                                                    isInvalid={isRequiredFormValueInvalid(formState.precio)}
                                                    required
                                                    onChange={onInputChanged}
                                                    prefix={'$'}
                                                    decimalSeparator={"."}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Campo debe ser un número.
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <ButtonsCol className="d-flex justify-content-center">
                                    <SuccessButton id="buttonFinalizar" type="submit" variant="success"
                                    >Guardar</SuccessButton>
                                </ButtonsCol>
                            </Form>
                        </Row>

                    </Card.Body>
                </Card>
            </div>
        </Row>
    );
}


const ButtonsCol = styled(Col)`
  gap: 1%;
`

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 0px;
`

const isFormValueValid = (formValue: FormValue): boolean => formValue.isValid === true && formValue.wasTouched
const isRequiredFormValueValid = (formValue: FormValue): boolean => formValue.isValid === true
const isRequiredFormValueInvalid = (formValue: FormValue): boolean => formValue.isValid === false
const isFormValueInvalid = (formValue: FormValue): boolean => formValue.isValid === false && formValue.wasTouched

const exitoOptions: ExitoOption[] = [
    {
        description: 'Crear nuevo servicio',
        path: '/servicios/transporte/crear',
        variant: 'primary'
    },
    {
        description: 'Ver listado',
        path: '/servicios/transporte',
        variant: 'secondary',
        isLast: true
    }
];

export default FormSection;